import React, { useEffect, useState } from 'react';
import { Trash2, Edit, Calendar, Send, Clock, Search } from 'lucide-react';
import axios from 'axios';

interface Campaign {
  id: string;
  name: string;
  message_template: string;
  status: 'scheduled' | 'processing' | 'completed' | 'failed';
  scheduled_at: string;
  created_at: string;
  total_leads: number;
  sent_leads: number;
}

interface Props {
  onEdit?: (campaign: any) => void;
  tenantId: string;
}

const CampaignHistory: React.FC<Props> = ({ tenantId, onEdit }) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`https://zapshop.app/api/campaigns/${tenantId}`);
      setCampaigns(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza? Isso apagará o histórico desta campanha.')) return;
    try {
      await axios.delete(`https://zapshop.app/api/campaigns/${id}`);
      setCampaigns(prev => prev.filter(c => c.id !== id));
      alert('Campanha removida.');
    } catch (error) {
      alert('Erro ao excluir.');
    }
  };

  useEffect(() => {
    if (tenantId) fetchCampaigns();
  }, [tenantId]);

  const getStatusBadge = (status: string) => {
    const styles = {
      scheduled: 'bg-indigo-500/10 text-indigo-600 border-indigo-200',
      processing: 'bg-amber-500/10 text-amber-600 border-amber-200',
      completed: 'bg-emerald-500/10 text-emerald-600 border-emerald-200',
      failed: 'bg-red-500/10 text-red-600 border-red-200',
    };
    const labels = {
      scheduled: 'Agendada',
      processing: 'Enviando...',
      completed: 'Concluída',
      failed: 'Falha',
    };

    return (
      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${styles[status as keyof typeof styles] || styles.scheduled} flex items-center gap-1 w-fit`}>
        {status === 'processing' && <Clock size={10} className="animate-pulse" />}
        {labels[status as keyof typeof labels] || status}
      </span>
    );
  };

  if (loading) return <div className="p-10 text-center text-gray-400 animate-pulse">Carregando histórico...</div>;

  const filteredCampaigns = campaigns.filter(c => c.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Barra de Topo */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-4 md:p-6 rounded-2xl border border-surface-100 shadow-sm gap-4">
        <div>
           <h2 className="text-surface-900 font-bold text-lg flex items-center gap-2">
             <Send size={20} className="text-brand-600"/> Minhas Campanhas
           </h2>
           <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wider">Acompanhamento em tempo real</p>
        </div>
        <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14}/>
            <input
                placeholder="Filtrar campanhas..."
                className="input-primary w-full pl-9 py-2 text-xs"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
            />
        </div>
      </div>

      {filteredCampaigns.length === 0 ? (
        <div className="text-center py-16 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
          <Send size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-400 font-medium">Nenhuma campanha encontrada.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredCampaigns.map((camp) => {
            const sent = Number(camp.sent_leads) || 0;
            const total = Number(camp.total_leads) || 0;
            const percentage = total > 0 ? Math.round((sent / total) * 100) : 0;

            return (
              <div key={camp.id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-bold text-gray-800 text-lg">{camp.name}</h3>
                        {getStatusBadge(camp.status)}
                    </div>
                    <p className="text-xs text-gray-400 flex items-center gap-1.5 font-mono">
                      <Calendar size={12} />
                      {new Date(camp.scheduled_at || camp.created_at).toLocaleString('pt-BR')}
                    </p>
                  </div>

                  <div className="flex items-center gap-1">
                     <button
                      onClick={() => onEdit && onEdit(camp)}
                      className="text-gray-400 hover:text-brand-600 p-2 hover:bg-brand-50 rounded-lg transition-colors"
                      title="Editar campanha"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(camp.id)}
                      className="text-gray-400 hover:text-red-500 p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title="Excluir histórico"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                {/* Barra de Progresso */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="flex justify-between text-xs font-bold text-gray-500 mb-2 uppercase tracking-wider">
                    <span>Progresso de Envio</span>
                    <span className={percentage === 100 ? 'text-emerald-600' : 'text-indigo-600'}>
                        {sent} / {total} <span className="text-gray-300 mx-1">|</span> {percentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-1000 ease-out relative ${
                        camp.status === 'completed' ? 'bg-emerald-500' : 'bg-indigo-500'
                      }`}
                      style={{ width: `${percentage}%` }}
                    >
                    </div>
                  </div>
                </div>

                {/* Prévia da Mensagem */}
                <div className="mt-4 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 text-xs text-gray-500 bg-gray-50 rounded-lg p-3 border border-gray-100/50 w-full overflow-hidden">
                    <span className="font-bold text-gray-400 shrink-0 mt-0.5">MSG:</span>
                    <span className="italic break-all whitespace-pre-wrap min-w-0 w-full">"{camp.message_template}"</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CampaignHistory;
